import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';

// Long enough that most reviews show in full (median length is ~170 chars),
// short enough that a 1200-character one doesn't blow the card open.
const CLAMP_LINES = 7;
const EXPAND_THRESHOLD = 260;

const Reviews = () => {
  const [reviewsData, setReviewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});
  const { t } = useTranslation();
  const reviewsRef = useRef();
  const swiperRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('animate-in')),
      { threshold: 0.1 }
    );
    if (reviewsRef.current) observer.observe(reviewsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch('https://hub.mileageriot.com/api/reviews');
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        const reviews = Array.isArray(data) ? data : (data.data || []);
        setReviewsData(reviews);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews');
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const toggleExpanded = (index) => {
    setExpandedReviews(prev => ({ ...prev, [index]: !prev[index] }));
    // The slide grew or shrank; without this Swiper keeps the old track height.
    requestAnimationFrame(() => swiperRef.current?.update());
  };

  if (!loading && !error && (!reviewsData || reviewsData.length === 0)) return null;

  return (
    <section className="reviews-modern py-5" ref={reviewsRef}>
      <div className="container">
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaStar className="me-2" />
            <span>{t('reviews.badge')}</span>
          </div>
          <h2 className="section-title text-glow text-white">{t('reviews.title')}</h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">{t('reviews.subtitle')}</p>
        </div>

        <div className="fade-in-up stagger-2">
          {loading && (
            <div className="text-center text-white">
              <div className="loading-spinner"><div className="spinner-modern"></div></div>
              <p className="mt-3 text-white-50">{t('reviews.loading')}</p>
            </div>
          )}
          {error && <div className="text-center"><p className="text-danger">{error}</p></div>}
          {!loading && !error && reviewsData.length > 0 && (
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              onSwiper={(s) => { swiperRef.current = s; }}
              spaceBetween={24}
              loop={reviewsData.length > 3}
              autoplay={{ delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              navigation
              pagination={{ clickable: true, dynamicBullets: true }}
              breakpoints={{
                0: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                992: { slidesPerView: 3 },
              }}
              className="reviews-swiper"
            >
              {reviewsData.map((review, index) => {
                const message = review.message || t('reviews.noMessage');
                const canExpand = (review.message || '').length > EXPAND_THRESHOLD;
                const isExpanded = expandedReviews[index];
                return (
                  <SwiperSlide key={review.id || index}>
                    <figure className="review-card-modern">
                      <FaQuoteLeft className="quote-icon" aria-hidden="true" />

                      <blockquote
                        className={`review-message${isExpanded ? ' is-expanded' : ''}`}
                        style={{ '--clamp-lines': CLAMP_LINES }}
                      >
                        {message}
                      </blockquote>

                      {canExpand && (
                        <button
                          type="button"
                          className="review-toggle"
                          onClick={() => toggleExpanded(index)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? t('reviews.viewLess') : t('reviews.viewMore')}
                        </button>
                      )}

                      <figcaption className="review-author">
                        <img
                          loading="lazy"
                          decoding="async"
                          src={review.avatar}
                          alt=""
                          className="review-avatar"
                          width="48"
                          height="48"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(review.name || 'Mileage Riot') + '&color=00cfe8&background=1d1e26&size=128';
                          }}
                        />
                        <span className="review-author-name">
                          {review.name || t('reviews.anonymous')}
                        </span>
                      </figcaption>
                    </figure>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
        </div>
      </div>
      <div className="reviews-bg-decoration"></div>
    </section>
  );
};

export default Reviews;
